import React from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useNavigation } from "../../utilities/NavigationContext";
import { useAuth } from "../../utilities/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

/**
 * Forgot Password (Class Component)
 * Uses:
 *  - Honeypot (company field)
 *  - reCAPTCHA v3
 */
class ForgotPassword extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: "",
      company: "", // honeypot
      isSubmitting: false,
    };
  }

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  handleSubmit = async (e) => {
    e.preventDefault();

    const { email, company, isSubmitting } = this.state;
    if (isSubmitting) return;

    // ✅ Honeypot check
    if (company && company.trim().length > 0) {
      return; // likely bot
    }

    const { executeRecaptcha } = this.props;
    if (!executeRecaptcha) {
      toast.error("reCAPTCHA is loading. Please try again.");
      return;
    }

    this.setState({ isSubmitting: true });

    let tokenCap;
    try {
      tokenCap = await executeRecaptcha("forgot_password");
    } catch {
      this.setState({ isSubmitting: false });
      toast.error("reCAPTCHA failed. Please try again.");
      return;
    }

    try {
      await axios.post(`${EndPoint}/user/forgot-password`, {
        email: email.trim(),
        tokenCap,
        company,
      });

      toast.success("Password reset link sent to your email.");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Error sending reset email."
      );
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const { email, company, isSubmitting } = this.state;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-900 to-sky-800 px-4">
        <ToastContainer />

        <div className="w-full max-w-md bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl p-8">
          <h2 className="text-3xl font-bold text-center text-white mb-4">
            Forgot Password
          </h2>

          <p className="text-sm text-white/70 text-center mb-6">
            Enter your registered email to receive a reset link.
          </p>

          <form onSubmit={this.handleSubmit} className="space-y-4">
            {/* Email Input */}
            <input
              type="email"
              name="email"
              placeholder="you@clinic.com.au"
              value={email}
              onChange={this.handleInputChange}
              required
              className="w-full rounded-xl bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-500 outline-none ring-1 ring-black/5 focus:ring-4 focus:ring-cyan-200"
            />

            {/* Honeypot (off-screen, not hidden) */}
            <div
              aria-hidden="true"
              className="absolute left-[-10000px] top-auto w-px h-px overflow-hidden"
            >
              <label>
                Company
                <input
                  type="text"
                  name="company"
                  value={company}
                  onChange={this.handleInputChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full rounded-xl py-3 text-sm font-semibold text-white transition ${
                isSubmitting
                  ? "bg-sky-600/60 cursor-not-allowed"
                  : "bg-sky-700 hover:bg-sky-600"
              }`}
            >
              {isSubmitting ? "Sending..." : "Reset Password"}
            </button>
          </form>

          {/* Navigation Links */}
          <div className="mt-6 text-center text-sm text-white/70">
            Remember your password?{" "}
            <button
              type="button"
              onClick={() => this.props.navigate("/login")}
              className="text-cyan-300 hover:text-cyan-200"
            >
              Login here
            </button>
          </div>

          <div className="mt-2 text-center text-sm text-white/70">
            Don't have an account?{" "}
            <Link
              to="/register"
              className="text-cyan-300 hover:text-cyan-200"
            >
              Register here
            </Link>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Wrapper (Hooks live here)
 */
const ForgotPasswordWithNavigation = (props) => {
  const navigate = useNavigation();
  const [, setAuth] = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();

  return (
    <ForgotPassword
      {...props}
      navigate={navigate}
      setAuth={setAuth}
      executeRecaptcha={executeRecaptcha}
    />
  );
};

export default ForgotPasswordWithNavigation;